import { Field } from '@/modules/primitives3D/editorShell';
import { FieldHint, FieldLabel, SectionTitle } from '@/components/ui/text';
import { Input } from '@/components/cms/ui/input';
import { Chip } from '@/components/ui/chip';
import { AutoGrowTextarea } from '../AutoGrowTextarea';
import {
  MIN_TEMPLATE_DESCRIPTION_LENGTH,
  MIN_TEMPLATE_LEARNINGS_LENGTH,
  TEMPLATE_DIFFICULTIES,
} from '../templateOptions';
import {
  ERROR_INPUT_CLASS,
  FIELD_ERROR_MESSAGES,
  type FieldKey,
} from '../validation';
import { CharCounter, FieldError, RequiredMark } from '../FieldMarks';
import type { TemplateDifficulty } from '../types';

interface Props {
  name: string;
  storyName: string;
  description: string;
  learnings: string;
  difficulty: TemplateDifficulty | '';
  // Inferred from the scene's contents (masks, keyframe animation). Pre-selected
  // by the caller and explained in the hint below; the creator still chooses.
  suggestedDifficulty?: TemplateDifficulty | null;
  suggestionReason?: string;
  errors: Set<FieldKey>;
  onChange: (patch: {
    name?: string;
    description?: string;
    learnings?: string;
    difficulty?: TemplateDifficulty;
  }) => void;
}

export function TemplateInfoStep({
  name,
  storyName,
  description,
  learnings,
  difficulty,
  suggestedDifficulty,
  suggestionReason,
  errors,
  onChange,
}: Props) {
  const suggestedLabel = TEMPLATE_DIFFICULTIES.find((d) => d.value === suggestedDifficulty)?.label;
  return (
    <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line h-full">
      <div className="space-y-4 min-w-0 pr-7">
        <SectionTitle>Details</SectionTitle>

        <Field>
          <FieldLabel mb>
            Template name
            <RequiredMark />
          </FieldLabel>
          <Input
            className={errors.has('templateName') ? ERROR_INPUT_CLASS : undefined}
            value={name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={storyName || 'Name this template'}
          />
          {errors.has('templateName') ? (
            <FieldError>{FIELD_ERROR_MESSAGES.templateName}</FieldError>
          ) : (
            <FieldHint>Your template can have a different name from its story.</FieldHint>
          )}
        </Field>

        <Field>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <FieldLabel>
              Description
              <RequiredMark />
            </FieldLabel>
            <CharCounter length={description.trim().length} min={MIN_TEMPLATE_DESCRIPTION_LENGTH} />
          </div>
          <AutoGrowTextarea
            className={errors.has('templateDescription') ? ERROR_INPUT_CLASS : undefined}
            value={description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="What the template is, how it behaves, and what a creator gets when they open it."
            minHeightPx={150}
            maxHeightPx={150}
          />
          {errors.has('templateDescription') ? (
            <FieldError>{FIELD_ERROR_MESSAGES.templateDescription}</FieldError>
          ) : (
            <FieldHint>
              At least {MIN_TEMPLATE_DESCRIPTION_LENGTH} characters. Describe what the template is
              and how it behaves.
            </FieldHint>
          )}
        </Field>
      </div>

      <div className="space-y-4 min-w-0 pl-7">
        <SectionTitle>What creators take away</SectionTitle>

        <Field>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <FieldLabel>
              What other creators will learn
              <RequiredMark />
            </FieldLabel>
            <CharCounter length={learnings.trim().length} min={MIN_TEMPLATE_LEARNINGS_LENGTH} />
          </div>
          <AutoGrowTextarea
            className={errors.has('templateLearnings') ? ERROR_INPUT_CLASS : undefined}
            value={learnings}
            onChange={(e) => onChange({ learnings: e.target.value })}
            placeholder="A paragraph on the technique someone takes away from opening this template."
            minHeightPx={112}
            maxHeightPx={112}
          />
          {errors.has('templateLearnings') ? (
            <FieldError>{FIELD_ERROR_MESSAGES.templateLearnings}</FieldError>
          ) : (
            <FieldHint>
              At least {MIN_TEMPLATE_LEARNINGS_LENGTH} characters. Write the paragraph you would
              want to read before opening someone else's template.
            </FieldHint>
          )}
        </Field>

        <div className="space-y-3">
          <FieldLabel>
            Difficulty
            <RequiredMark />
          </FieldLabel>
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_DIFFICULTIES.map((d) => (
              <Chip
                key={d.value}
                label={d.label}
                selected={difficulty === d.value}
                onClick={() => onChange({ difficulty: d.value })}
              />
            ))}
          </div>
          {errors.has('templateDifficulty') ? (
            <FieldError>{FIELD_ERROR_MESSAGES.templateDifficulty}</FieldError>
          ) : (
            suggestedLabel && (
              <FieldHint>
                Suggested: {suggestedLabel}. {suggestionReason}
              </FieldHint>
            )
          )}
        </div>
      </div>
    </div>
  );
}
